import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit2, Trash2, Upload, IdCard as IdCardIcon } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", designation: "Reporter", phone: "", email: "", photo_url: "", blood_group: "", address: "", valid_till: "", status: "active", reporter_pin: "" };

const DESIGNATIONS = ["Reporter", "Senior Reporter", "Photographer", "Editor", "Sub-Editor", "Camera Person", "Field Correspondent", "Stringer"];

export default function StaffPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/staff").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    const d = new Date(); d.setFullYear(d.getFullYear() + 1);
    setForm({ ...EMPTY, valid_till: d.toISOString().slice(0, 10) });
    setEditId(null); setOpen(true);
  };
  const openEdit = (s) => { setForm({...EMPTY, ...s, valid_till: (s.valid_till || "").slice(0,10)}); setEditId(s.id); setOpen(true); };

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      // Staff/reporter ID card photos should NOT be watermarked
      const { data } = await api.post("/upload?watermark=false", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, photo_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
    } catch(err) { toast.error(formatError(err)); }
  };

  const submit = async () => {
    try {
      if (editId) { await api.put(`/staff/${editId}`, form); toast.success("अपडेट हो गया"); }
      else { await api.post("/staff", form); toast.success("स्टाफ जोड़ा गया"); }
      setOpen(false); load();
    } catch(err) { toast.error(formatError(err)); }
  };
  const del = async (id) => {
    if (!window.confirm("क्या हटाना है?")) return;
    try { await api.delete(`/staff/${id}`); toast.success("हटा दिया"); load(); }
    catch(err) { toast.error(formatError(err)); }
  };

  return (
    <div data-testid="staff-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">रिपोर्टर / स्टाफ</h1>
          <p className="text-sm text-slate-500 mt-1">कुल {items.length} — ID कार्ड जनरेट करने के लिए 'ID कार्ड' बटन दबाएँ</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F172A] hover:bg-[#1E3A8A]" onClick={openNew} data-testid="add-staff-btn"><Plus className="w-4 h-4 mr-1.5"/>नया स्टाफ</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editId ? "स्टाफ संपादित करें" : "नया स्टाफ जोड़ें"}</DialogTitle><DialogDescription>पत्रकार / रिपोर्टर विवरण; ID कार्ड के लिए फोटो अपलोड करें</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>नाम</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="st-name"/></div>
                <div>
                  <Label>पदनाम</Label>
                  <select className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white" value={form.designation} onChange={(e) => setForm({...form, designation: e.target.value})} data-testid="st-designation">
                    {DESIGNATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>फ़ोन</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="st-phone"/></div>
                <div><Label>ईमेल</Label><Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>ब्लड ग्रुप</Label><Input value={form.blood_group} onChange={(e) => setForm({...form, blood_group: e.target.value})} placeholder="B+"/></div>
                <div><Label>वैध तिथि तक</Label><Input type="date" value={form.valid_till} onChange={(e) => setForm({...form, valid_till: e.target.value})} data-testid="st-valid-till"/></div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <Label className="text-amber-800">रिपोर्टर ऐप PIN (4-6 अंक)</Label>
                <Input value={form.reporter_pin || ""} onChange={(e) => setForm({...form, reporter_pin: e.target.value})} placeholder="जैसे: 1234" maxLength={6} className="font-mono" data-testid="st-reporter-pin"/>
                <p className="text-[10px] text-amber-700 mt-1.5">यह PIN फ़ील्ड रिपोर्टर को <b>/reporter</b> पेज पर लॉगिन करने के लिए दें। खाली रखने पर वे लॉगिन नहीं कर पाएँगे।</p>
              </div>
              <div><Label>पता</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({...form, address: e.target.value})}/></div>
              <div>
                <Label>फोटो</Label>
                <div className="flex items-center gap-3">
                  <Input value={form.photo_url} onChange={(e) => setForm({...form, photo_url: e.target.value})} placeholder="URL या अपलोड"/>
                  <label className="cursor-pointer flex items-center gap-1.5 border border-slate-300 rounded-md px-3 py-2 text-sm hover:bg-slate-50">
                    <Upload className="w-4 h-4"/>
                    <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="st-upload"/>
                  </label>
                </div>
                {form.photo_url && <img src={form.photo_url} alt="" className="mt-2 h-24 w-24 rounded object-cover"/>}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>रद्द</Button>
              <Button onClick={submit} className="bg-[#0F172A] hover:bg-[#1E3A8A]" data-testid="save-staff">सहेजें</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>ID</TableHead><TableHead>नाम</TableHead><TableHead>पदनाम</TableHead>
            <TableHead>फ़ोन</TableHead><TableHead>वैधता</TableHead><TableHead>स्थिति</TableHead><TableHead className="text-right">कार्य</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                <TableCell className="font-mono text-xs">{s.staff_id}</TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {s.photo_url && <img src={s.photo_url} alt="" className="w-8 h-8 rounded-full object-cover"/>}
                    {s.name}
                  </div>
                </TableCell>
                <TableCell>{s.designation}</TableCell>
                <TableCell>{s.phone}</TableCell>
                <TableCell className="text-xs">{s.valid_till}</TableCell>
                <TableCell><Badge variant={s.status === "active" ? "default" : "secondary"}>{s.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <Link to={`/admin/staff/${s.id}/id-card`}>
                    <Button variant="outline" size="sm" className="mr-2" data-testid={`idcard-${s.id}`}><IdCardIcon className="w-4 h-4 mr-1"/>ID कार्ड</Button>
                  </Link>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`}><Edit2 className="w-4 h-4"/></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(s.id)} data-testid={`del-staff-${s.id}`}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">कोई स्टाफ नहीं</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
